/**
 * Positivliste für Vertragstexte (VB-25): angezeigt = gedruckt = gehasht.
 *
 * Ein Vertragstext darf nur Auszeichnungen dieser Liste tragen. Dieselbe Liste gilt
 * - in der Anzeige (apps/web/src/lib/sicheres-html.ts bereinigt danach, statt jede Klasse durchzulassen),
 * - auf dem Server (app.contract_markup_rules() und app.contract_markup_problem(), Migration 0111): Aktivieren einer Vorlage
 *   und Versand eines Vertrags lehnen alles außerhalb der Liste ab, statt es still zu verwerfen,
 * - im Baukasten (contract-blocks-compile.ts erzeugt nur Markup aus dieser Liste).
 * Gleichlauf-Test: tests/db/99zzzzzz_vertrag_positivliste.test.ts vergleicht Liste und Befunde mit der Datenbank.
 *
 * Warum so streng: Klassen wie „sr-only“ oder „tk-mobile-only“, Kommentare, <noscript>, <template> oder <textarea> machen Text
 * auf dem Bildschirm unsichtbar, der trotzdem im Abschluss-Hash steht (und teils im PDF). Genau das schließt die Liste aus.
 * Nicht in der Liste sind außerdem <s> (durchgestrichen am Bildschirm, im PDF normal gedruckt) und start/type an <ol>
 * (andere Nummern am Bildschirm als im PDF).
 *
 * Außerdem hier: die Kopfzeile des vertraulichen Teils (VB-01, VB-20). Sie steht im gerenderten Text, wird also mitgedruckt
 * und mitgehasht; der Server setzt sie beim Versand (contract_send, 0111), die Vorschau zeigt sie gleich.
 */
import type { ContractRole } from "./contract-variables.js";
import type { Language } from "./types.js";

export interface ContractMarkupRules {
  /** erlaubte Elemente */
  tags: string[];
  /** Elemente ohne Schluss-Tag */
  voidTags: string[];
  /** je Element erlaubte Attribute außer class */
  attributes: Record<string, string[]>;
  /** je Klasse die Elemente, an denen sie stehen darf (genau eine Klasse je Element) */
  classes: Record<string, string[]>;
  /** Klassen, die nur das System setzt (Kopfzeile des vertraulichen Teils) */
  reservedClasses: string[];
  /** Werte von data-role */
  roles: string[];
  /** Werte von scope an th */
  scopes: string[];
}

/** Die eine Positivliste. Reihenfolge ist Teil des Gleichlaufs mit app.contract_markup_rules(). */
export const CONTRACT_MARKUP: ContractMarkupRules = {
  tags: ["h1", "h2", "h3", "h4", "p", "br", "hr", "strong", "b", "em", "i", "u", "small", "sup", "sub", "ol", "ul", "li", "div", "span",
    "blockquote", "table", "thead", "tbody", "tfoot", "tr", "td", "th", "caption"],
  voidTags: ["br", "hr"],
  attributes: { div: ["data-role"], span: ["data-var"], td: ["colspan", "rowspan"], th: ["colspan", "rowspan", "scope"] },
  classes: {
    "tk-signature": ["div"],
    "tk-fill": ["span"],
    "tk-page-break": ["div"],
    "tk-part-note": ["div"],
    "tk-align-center": ["h1", "h2", "h3", "h4", "p"],
    "tk-align-right": ["h1", "h2", "h3", "h4", "p"],
    "tk-sample-notice": ["p"],
  },
  reservedClasses: ["tk-part-note"],
  roles: ["agency", "brand", "talent"],
  scopes: ["row", "col", "rowgroup", "colgroup"],
};

/**
 * Größengrenzen (VB-26), gleich in der Datenbank (app.contract_limits(), Prüfregeln an contract_templates und contracts).
 * Zeichen, nicht Bytes.
 */
export const CONTRACT_LIMITS = {
  /** Vorlagentext bzw. Vertragsquelltext je Teil (Hauptteil, vertraulicher Teil) */
  htmlChars: 400_000,
  /** gerenderter Text je Teil (eingesetzte Werte können den Text verlängern) */
  renderedChars: 800_000,
  /** eigene Angaben je Vorlage bzw. Vertrag */
  variables: 200,
  /** Bausteine je Vorlage (alle Ebenen) */
  blocks: 600,
  /** Verschachtelung der Bausteine und der „Nur wenn“ im Text */
  depth: 4,
  /** Zeichen Text je Baustein */
  textChars: 20_000,
  /** Punkte je Aufzählung, Zeilen je Tabelle */
  items: 200,
  /** Zellen je Tabellenzeile */
  cells: 20,
  /** Leerraum zwischen Bausteinen (nur für die Rundlauftreue der Bestandsvorlagen) */
  ws: 40,
  /**
   * Beschriftung zwischen zwei Unterschriftsfeldern (Runde 2, R2-P2: „Für die Marke:“, „Ort, Datum“, Name und Funktion):
   * höchstens so viele sichtbare Zeichen ohne Leerraum je Lücke; nach dem letzten Feld steht nichts. Gleich app.contract_limit.
   */
  signatureLabel: 80,
} as const;

const TAGS = new Set(CONTRACT_MARKUP.tags);
const VOID = new Set(CONTRACT_MARKUP.voidTags);
const RESERVED = new Set(CONTRACT_MARKUP.reservedClasses);
const KEY_RX = /^[a-z][a-z0-9_]{0,63}$/;
const SPAN_RX = /^[1-9][0-9]{0,2}$/;

/** Liste zu einem Schlüssel, nur eigene Einträge („constructor“ ist keine Klasse) */
export function markupListOf(map: Record<string, string[]>, key: string): string[] {
  return Object.hasOwn(map, key) ? map[key]! : [];
}
const own = markupListOf;

/** Kanonisches Tag: kleingeschrieben, Attribute nur als name="wert". Gleicher Ausdruck in der Datenbank. */
export const CONTRACT_TAG_PATTERN = '<(/?)([a-z][a-z0-9]*)((?:[ \\t\\r\\n\\f]+[a-z][a-z0-9-]*="[^"<>]*")*)[ \\t\\r\\n\\f]*(/?)>';
const TAG_RX = () => new RegExp(CONTRACT_TAG_PATTERN, "g");
const ATTR_RX = () => /([a-z][a-z0-9-]*)="([^"<>]*)"/g;
// ---------------------------------------------------------------------------
// Zeichen und Zeichenreferenzen (Prüfung 0–2, Befunde 2, 11, 14, 15, 16; gleich in der Datenbank, Migration 0113)
// ---------------------------------------------------------------------------

/**
 * Leerraum wie „\s“ in JavaScript, aber als ausdrückliche Liste: PostgreSQL kennt U+00A0 in „\s“ nicht (Befund 11). Dieselbe
 * Klasse steht in der Datenbank (app.contract_ws_class).
 */
export const CONTRACT_WS_CLASS = "[\\t\\n\\v\\f\\r \\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff]";

export type ContractCharClass = "bidi" | "invisible" | "control";

/**
 * Zeichen, die im Vertragstext nicht stehen dürfen, je Art (Bereiche einschließlich). Gleich app.contract_char_ranges().
 * - bidi: Steuerzeichen der Schreibrichtung (Einbettung, Überschreibung, Isolierung, Richtungsmarken)
 * - invisible: unsichtbare Zeichen (Nullbreite, Wortverbinder, BOM, Soft-Hyphen, Füllzeichen, Variantenwähler, Unicode-Tags)
 * - control: Steuerzeichen C0/C1 außer Tab, Zeilenumbruch, Wagenrücklauf und Seitenvorschub, Nichtzeichen
 * Am Bildschirm unsichtbar oder anders angeordnet, im Prüfwert enthalten und im PDF anders gedruckt: angezeigt ≠ gedruckt.
 */
export const CONTRACT_CHAR_RANGES: Record<ContractCharClass, readonly (readonly [number, number])[]> = {
  bidi: [[0x061c, 0x061c], [0x200e, 0x200f], [0x202a, 0x202e], [0x2066, 0x2069]],
  invisible: [[0x00ad, 0x00ad], [0x034f, 0x034f], [0x115f, 0x1160], [0x17b4, 0x17b5], [0x180b, 0x180f], [0x200b, 0x200d],
    [0x2060, 0x2064], [0x206a, 0x206f], [0x3164, 0x3164], [0xfe00, 0xfe0f], [0xfeff, 0xfeff], [0xffa0, 0xffa0], [0xfff9, 0xfffb],
    [0xe0000, 0xe0fff]],
  control: [[0x0000, 0x0008], [0x000b, 0x000b], [0x000e, 0x001f], [0x007f, 0x009f], [0xfdd0, 0xfdef], [0xfffe, 0xffff]],
};
const CHAR_ORDER: readonly ContractCharClass[] = ["bidi", "invisible", "control"];

/** Art eines Zeichens nach CONTRACT_CHAR_RANGES oder null. */
export function contractCharClass(cp: number): ContractCharClass | null {
  for (const cls of CHAR_ORDER) for (const [a, b] of CONTRACT_CHAR_RANGES[cls]) if (cp >= a && cp <= b) return cls;
  return null;
}

const hex4 = (n: number) => n.toString(16).padStart(4, "0");
const charClassRx = (cls: ContractCharClass) =>
  new RegExp(`[${CONTRACT_CHAR_RANGES[cls].map(([a, b]) => (a === b ? `\\u{${hex4(a)}}` : `\\u{${hex4(a)}}-\\u{${hex4(b)}}`)).join("")}]`, "u");
const CHAR_RX: Record<ContractCharClass, RegExp> = { bidi: charClassRx("bidi"), invisible: charClassRx("invisible"), control: charClassRx("control") };

/**
 * Benannte Zeichenreferenzen, die Vertragstexte tragen dürfen, mit ihrem Zeichen. Genau diese löst das PDF auf (Befund 16);
 * jede andere benannte Referenz lehnen Versand und Aktivieren ab, weil der Browser sie anders zeigen würde, als das PDF sie
 * druckt. Gleich app.contract_named_refs().
 */
export const CONTRACT_NAMED_REFS: Readonly<Record<string, number>> = {
  amp: 0x26, lt: 0x3c, gt: 0x3e, quot: 0x22, apos: 0x27, nbsp: 0xa0,
  euro: 0x20ac, copy: 0xa9, reg: 0xae, trade: 0x2122, sect: 0xa7, para: 0xb6, deg: 0xb0, plusmn: 0xb1, times: 0xd7, divide: 0xf7,
  middot: 0xb7, bull: 0x2022, hellip: 0x2026, ndash: 0x2013, mdash: 0x2014, lsquo: 0x2018, rsquo: 0x2019, sbquo: 0x201a,
  ldquo: 0x201c, rdquo: 0x201d, bdquo: 0x201e, laquo: 0xab, raquo: 0xbb, lsaquo: 0x2039, rsaquo: 0x203a, pound: 0xa3, yen: 0xa5,
  cent: 0xa2, micro: 0xb5, frac12: 0xbd, frac14: 0xbc, frac34: 0xbe, sup1: 0xb9, sup2: 0xb2, sup3: 0xb3, ordf: 0xaa, ordm: 0xba,
  iexcl: 0xa1, iquest: 0xbf, not: 0xac, prime: 0x2032, Prime: 0x2033, permil: 0x2030, dagger: 0x2020, Dagger: 0x2021,
  minus: 0x2212, thinsp: 0x2009, ensp: 0x2002, emsp: 0x2003,
  auml: 0xe4, ouml: 0xf6, uuml: 0xfc, Auml: 0xc4, Ouml: 0xd6, Uuml: 0xdc, szlig: 0xdf,
  aacute: 0xe1, agrave: 0xe0, acirc: 0xe2, atilde: 0xe3, aring: 0xe5, aelig: 0xe6, ccedil: 0xe7, eacute: 0xe9, egrave: 0xe8,
  ecirc: 0xea, euml: 0xeb, iacute: 0xed, igrave: 0xec, icirc: 0xee, iuml: 0xef, ntilde: 0xf1, oacute: 0xf3, ograve: 0xf2,
  ocirc: 0xf4, otilde: 0xf5, oslash: 0xf8, uacute: 0xfa, ugrave: 0xf9, ucirc: 0xfb, yacute: 0xfd, yuml: 0xff,
  Aacute: 0xc1, Agrave: 0xc0, Acirc: 0xc2, Atilde: 0xc3, Aring: 0xc5, AElig: 0xc6, Ccedil: 0xc7, Eacute: 0xc9, Egrave: 0xc8,
  Ecirc: 0xca, Euml: 0xcb, Iacute: 0xcd, Igrave: 0xcc, Icirc: 0xce, Iuml: 0xcf, Ntilde: 0xd1, Oacute: 0xd3, Ograve: 0xd2,
  Ocirc: 0xd4, Otilde: 0xd5, Oslash: 0xd8, Uacute: 0xda, Ugrave: 0xd9, Ucirc: 0xdb, Yacute: 0xdd,
  oelig: 0x153, OElig: 0x152, scaron: 0x161, Scaron: 0x160, zcaron: 0x17e, Zcaron: 0x17d, Yuml: 0x178,
};

/**
 * Benannte Referenzen, die der Browser als unsichtbares oder richtungsänderndes Zeichen zeigt (nicht in CONTRACT_NAMED_REFS;
 * die Zeichenprüfung erkennt sie trotzdem als solche). „&shy“ gilt auch ohne Semikolon (Altform des Browsers).
 */
export const CONTRACT_HIDDEN_NAMED_REFS: Readonly<Record<string, number>> = {
  shy: 0xad, lrm: 0x200e, rlm: 0x200f, zwj: 0x200d, zwnj: 0x200c, ZeroWidthSpace: 0x200b, NegativeVeryThinSpace: 0x200b,
  NegativeThinSpace: 0x200b, NegativeMediumSpace: 0x200b, NegativeThickSpace: 0x200b, NoBreak: 0x2060, af: 0x2061,
  ApplyFunction: 0x2061, it: 0x2062, InvisibleTimes: 0x2062, ic: 0x2063, InvisibleComma: 0x2063,
};

/** Windows-1252 für die Referenzen &#128; … &#159; (so löst der Browser sie auf). */
const CP1252: Readonly<Record<number, number>> = {
  0x80: 0x20ac, 0x82: 0x201a, 0x83: 0x0192, 0x84: 0x201e, 0x85: 0x2026, 0x86: 0x2020, 0x87: 0x2021, 0x88: 0x02c6, 0x89: 0x2030,
  0x8a: 0x0160, 0x8b: 0x2039, 0x8c: 0x0152, 0x8e: 0x017d, 0x91: 0x2018, 0x92: 0x2019, 0x93: 0x201c, 0x94: 0x201d, 0x95: 0x2022,
  0x96: 0x2013, 0x97: 0x2014, 0x98: 0x02dc, 0x99: 0x2122, 0x9a: 0x0161, 0x9b: 0x203a, 0x9c: 0x0153, 0x9e: 0x017e, 0x9f: 0x0178,
};

/** Numerische Referenz wie im Browser: dezimal oder hex, Semikolon freiwillig, führende Nullen erlaubt. */
const NUMERIC_REF = "&#(?:[xX]([0-9a-fA-F]+)|([0-9]+));?";
/** Jede Referenz (numerisch oder benannt) in der Reihenfolge des Texts; gleicher Ausdruck in der Datenbank. */
export const CONTRACT_REF_PATTERN = "&(?:#(?:[xX][0-9a-fA-F]+|[0-9]+);?|[A-Za-z][A-Za-z0-9]*;?)";

/** Wert einer numerischen Referenz (ohne Umdeutung); über 0x10FFFF gilt 0x110000. */
function refNumber(hexDigits: string | undefined, decDigits: string | undefined): number {
  const digits = (hexDigits ?? decDigits ?? "").replace(/^0+/, "");
  if (digits === "") return 0;
  if (digits.length > 8) return 0x110000;
  const n = hexDigits !== undefined ? parseInt(digits, 16) : parseInt(digits, 10);
  return n > 0x10ffff ? 0x110000 : n;
}

/** Zeichen, das der Browser für eine numerische Referenz zeigt: ungültig → U+FFFD, 128–159 → Windows-1252. */
function browserCodePoint(n: number): number {
  if (n === 0 || n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff)) return 0xfffd;
  if (n >= 0x80 && n <= 0x9f) return CP1252[n] ?? n;
  return n;
}

/**
 * Erstes Steuer- oder unsichtbares Zeichen: roh oder als Zeichenreferenz (dezimal, hex, ohne Semikolon, mit führenden
 * Nullen, benannt wie &rlm; oder &shy;). `char:bidi` vor `char:invisible` vor `char:control`; sonst null.
 */
export function contractCharProblem(html: string | null | undefined): string | null {
  if (!html) return null;
  const found = new Set<ContractCharClass>();
  for (const cls of CHAR_ORDER) if (CHAR_RX[cls].test(html)) found.add(cls);
  if (html.includes("&")) {
    for (const m of html.matchAll(new RegExp(NUMERIC_REF, "g"))) {
      const cls = contractCharClass(browserCodePoint(refNumber(m[1], m[2])));
      if (cls) found.add(cls);
    }
    for (const m of html.matchAll(/&([A-Za-z][A-Za-z0-9]*);/g)) {
      const cp = Object.hasOwn(CONTRACT_HIDDEN_NAMED_REFS, m[1]!) ? CONTRACT_HIDDEN_NAMED_REFS[m[1]!]! : null;
      const cls = cp === null ? null : contractCharClass(cp);
      if (cls) found.add(cls);
    }
    if (html.includes("&shy")) found.add("invisible");
  }
  for (const cls of CHAR_ORDER) if (found.has(cls)) return `char:${cls}`;
  return null;
}

/**
 * Steuer- oder unsichtbares Zeichen in einem eingetragenen Wert (roh; ein Wert ist Text, keine Auszeichnung, und wird beim
 * Einsetzen maskiert). Gleich app.contract_value_char_problem: So lehnt das Ausfüllen durch Parteien solche Werte ab (Befund 3).
 */
export function contractValueCharProblem(value: string | null | undefined): string | null {
  if (!value) return null;
  for (const cls of CHAR_ORDER) if (CHAR_RX[cls].test(value)) return `char:${cls}`;
  return null;
}

/**
 * Erste Zeichenreferenz, die Browser und PDF verschieden zeigen würden, oder null (Befunde 15, 16):
 * - numerisch außerhalb von Unicode, 0, Ersatzzeichen (Surrogate) oder 128–159 (der Browser deutet sie als Windows-1252);
 * - benannt, aber nicht in CONTRACT_NAMED_REFS oder ohne Semikolon (etwa „&T“ in „AT&T“: bitte „&amp;“ schreiben).
 * Kennung `ref:<Ausschnitt>`. Getrennt von contractMarkupProblem, weil der Text dabei gültiges Markup bleibt; Versand
 * (contract_send) und Aktivieren prüfen beides.
 */
export function contractRefProblem(html: string | null | undefined): string | null {
  if (!html || !html.includes("&")) return null;
  for (const m of html.matchAll(new RegExp(CONTRACT_REF_PATTERN, "g"))) {
    const ref = m[0];
    const num = /^&#(?:[xX]([0-9a-fA-F]+)|([0-9]+))/.exec(ref);
    if (num) {
      const n = refNumber(num[1], num[2]);
      if (n === 0 || n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff) || (n >= 0x80 && n <= 0x9f)) return `ref:${Array.from(ref).slice(0, 24).join("")}`;
      continue;
    }
    const name = ref.endsWith(";") ? ref.slice(1, -1) : null;
    if (name === null || !Object.hasOwn(CONTRACT_NAMED_REFS, name)) return `ref:${Array.from(ref).slice(0, 24).join("")}`;
  }
  return null;
}

/**
 * Zeichen einer Zeichenreferenz, wie der Browser sie zeigt (numerisch mit Umdeutung, benannt nach CONTRACT_NAMED_REFS), oder
 * null für benannte Referenzen außerhalb der Liste (die lehnt contractRefProblem ab).
 */
export function contractRefCodePoint(ref: string): number | null {
  const num = /^&#(?:[xX]([0-9a-fA-F]+)|([0-9]+))/.exec(ref);
  if (num) return browserCodePoint(refNumber(num[1], num[2]));
  const name = ref.endsWith(";") ? ref.slice(1, -1) : null;
  return name !== null && Object.hasOwn(CONTRACT_NAMED_REFS, name) ? CONTRACT_NAMED_REFS[name]! : null;
}

/**
 * Zeichenreferenzen auflösen wie der Browser, soweit Vertragstexte sie tragen dürfen (für das PDF, Befunde 15 und 16):
 * numerische Referenzen (mit und ohne Semikolon; ungültige werden U+FFFD, nie ein Absturz) und die benannten aus
 * CONTRACT_NAMED_REFS. Alles andere bleibt wörtlich stehen (der Versand lässt es gar nicht erst zu).
 */
export function decodeContractRefs(text: string): string {
  if (!text.includes("&")) return text;
  return text.replace(new RegExp(CONTRACT_REF_PATTERN, "g"), (ref) => {
    const num = /^&#(?:[xX]([0-9a-fA-F]+)|([0-9]+))/.exec(ref);
    if (num) return String.fromCodePoint(browserCodePoint(refNumber(num[1], num[2])));
    const name = ref.endsWith(";") ? ref.slice(1, -1) : null;
    return name !== null && Object.hasOwn(CONTRACT_NAMED_REFS, name) ? String.fromCodePoint(CONTRACT_NAMED_REFS[name]!) : ref;
  });
}

/**
 * Unterschriftsfeld oder Seitenumbruch mit Inhalt (Befund 17): Die Anzeige ersetzt nur leere Felder, das PDF zeichnet jedes;
 * ein Seitenumbruch hat am Bildschirm die Höhe 0. Beide Elemente sind deshalb nur leer erlaubt. Gleicher Ausdruck in der
 * Datenbank; jedes Attribut-Schema der Positivliste, jede Reihenfolge.
 */
const FIELD_CONTENT_RX = /<div(?:[ \t\r\n\f]+[a-z][a-z0-9-]*="[^"<>]*")*[ \t\r\n\f]+class="(tk-signature|tk-page-break)"(?:[ \t\r\n\f]+[a-z][a-z0-9-]*="[^"<>]*")*[ \t\r\n\f]*>(?!<\/div>)/;

export interface MarkupCheckOptions {
  /** reservierte Klassen erlauben (nur für bereits vom System gesetzte Kopfzeilen) */
  allowReserved?: boolean;
}

/**
 * Erster Verstoß gegen die Positivliste oder null. Kennungen (gleich in app.contract_markup_problem):
 * - `char:bidi` Steuerzeichen der Schreibrichtung, `char:invisible` unsichtbare Zeichen, `char:control` Steuerzeichen, jeweils
 *   roh oder als Zeichenreferenz (contractCharProblem)
 * - `content:<klasse>` Unterschriftsfeld oder Seitenumbruch mit Inhalt (nur leer erlaubt)
 * - `syntax:<Ausschnitt>` ein „<“, das kein kanonisches Tag beginnt (Kommentar, Großschreibung, einfache Anführungszeichen,
 *   nicht maskiertes „<“)
 * - `tag:<name>` Element nicht erlaubt, `tag:/<name>` Schluss-Tag mit Attributen oder zu einem leeren Element,
 *   `tag:<name>/` selbstschließendes Element, das Inhalt hat
 * - `attr:<tag>@<name>` Attribut nicht erlaubt (auch doppelt, data-role ohne tk-signature, data-var ohne tk-fill)
 * - `class:<wert>` Klasse nicht erlaubt (unbekannt, mehrere, falsches Element)
 * - `reserved:<klasse>` Kopfzeile des vertraulichen Teils von Hand
 * - `value:<tag>@<name>` Attributwert ungültig
 */
export function contractMarkupProblem(html: string | null | undefined, opts: MarkupCheckOptions = {}): string | null {
  if (!html) return null;
  const chars = contractCharProblem(html);
  if (chars) return chars;
  const stripped = html.replace(TAG_RX(), "");
  const at = stripped.indexOf("<");
  if (at >= 0) return `syntax:${Array.from(stripped.slice(at)).slice(0, 24).join("")}`;
  for (const m of html.matchAll(TAG_RX())) {
    const close = m[1] === "/";
    const tag = m[2]!;
    const attrs = m[3] ?? "";
    const selfClose = m[4] === "/";
    if (!TAGS.has(tag)) return `tag:${tag}`;
    const isVoid = VOID.has(tag);
    if (close && (attrs !== "" || selfClose || isVoid)) return `tag:/${tag}`;
    if (selfClose && !isVoid) return `tag:${tag}/`;
    const values = new Map<string, string>();
    let cls: string | null = null;
    for (const a of attrs.matchAll(ATTR_RX())) {
      const name = a[1]!;
      const value = a[2]!;
      if (values.has(name)) return `attr:${tag}@${name}`;
      values.set(name, value);
      if (name === "class") cls = value;
      else if (!own(CONTRACT_MARKUP.attributes, tag).includes(name)) return `attr:${tag}@${name}`;
    }
    if (cls !== null) {
      if (!own(CONTRACT_MARKUP.classes, cls).includes(tag)) return `class:${Array.from(cls).slice(0, 40).join("")}`;
      if (RESERVED.has(cls) && !opts.allowReserved) return `reserved:${cls}`;
    }
    if (values.has("data-role") && cls !== "tk-signature") return `attr:${tag}@data-role`;
    if (values.has("data-var") && cls !== "tk-fill") return `attr:${tag}@data-var`;
    if (cls === "tk-signature" && !CONTRACT_MARKUP.roles.includes(values.get("data-role") ?? "")) return `value:${tag}@data-role`;
    if (cls === "tk-fill" && !KEY_RX.test(values.get("data-var") ?? "")) return `value:${tag}@data-var`;
    for (const name of ["colspan", "rowspan"]) {
      const v = values.get(name);
      if (v !== undefined && !SPAN_RX.test(v)) return `value:${tag}@${name}`;
    }
    const scope = values.get("scope");
    if (scope !== undefined && !CONTRACT_MARKUP.scopes.includes(scope)) return `value:${tag}@scope`;
  }
  const field = FIELD_CONTENT_RX.exec(html);
  if (field) return `content:${field[1]}`;
  return contractNestingProblem(html);
}

/** Elemente, die einen eigenen Block bilden (dürfen nicht in Absatz, Überschrift oder Auszeichnung im Text stehen). */
const BLOCK_TAGS = new Set(["p", "div", "h1", "h2", "h3", "h4", "ol", "ul", "table", "blockquote", "hr"]);
/** Elemente, in denen kein Block stehen darf (der Browser schlösse sie davor, das PDF nicht). */
const NO_BLOCK_INSIDE = new Set(["p", "h1", "h2", "h3", "h4", "strong", "b", "em", "i", "u", "small", "sup", "sub", "span"]);
const TABLE_PARTS = new Set(["table", "thead", "tbody", "tfoot", "tr"]);
/** Zeichen, die in Hoch- bzw. Tiefstellung stehen dürfen: genau die, die das PDF als hoch- bzw. tiefgestelltes Zeichen druckt. */
export const CONTRACT_SUP_CHARS: Readonly<Record<string, string>> = {
  "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴", "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹", "+": "⁺", "-": "⁻", "=": "⁼",
  "(": "⁽", ")": "⁾", n: "ⁿ", i: "ⁱ", " ": " ",
};
export const CONTRACT_SUB_CHARS: Readonly<Record<string, string>> = {
  "0": "₀", "1": "₁", "2": "₂", "3": "₃", "4": "₄", "5": "₅", "6": "₆", "7": "₇", "8": "₈", "9": "₉", "+": "₊", "-": "₋", "=": "₌",
  "(": "₍", ")": "₎", a: "ₐ", e: "ₑ", o: "ₒ", x: "ₓ", h: "ₕ", k: "ₖ", l: "ₗ", m: "ₘ", n: "ₙ", p: "ₚ", s: "ₛ", t: "ₜ", " ": " ",
};
/** Phase einer Tabelle: 0 Anfang, 1 nach caption, 2 nach thead, 3 nach Zeilen (tbody, tr), 4 nach tfoot */
const TABLE_PHASE: Readonly<Record<string, [number, number]>> = { caption: [0, 1], thead: [1, 2], tbody: [3, 3], tr: [3, 3], tfoot: [3, 4] };

/**
 * Verschachtelung, die Bildschirm und PDF gleich zeigen (Runde 2, R2-06, R2-09, R2-10; gleich app.contract_nesting_problem):
 * - Tabellen: nur caption, thead, tbody bzw. tr, tfoot in dieser Reihenfolge (der Browser zeichnet thead und caption oben und
 *   tfoot unten, egal wo sie stehen); Text nie direkt in table, thead, tbody, tfoot, tr (der Browser setzt ihn vor die Tabelle)
 * - Listen: li nur in ol/ul, Text nie direkt in ol/ul
 * - kein Block in Absatz, Überschrift oder Auszeichnung; Unterschriftsfelder nur auf oberster Ebene (nie in Tabelle oder Liste)
 * - Hoch- und Tiefstellung nur mit Zeichen, die das PDF hoch- bzw. tiefgestellt druckt, ohne weitere Auszeichnung darin
 * Kennungen `nest:<eltern>><kind>`, `nest:text@<eltern>`, `order:<tag>`, `nest:tk-signature`, `script:<zeichen>`.
 */
export function contractNestingProblem(html: string): string | null {
  const stack: { tag: string; phase: number }[] = [];
  let last = 0;
  // Platzhalter der Quelle ({{…}}) zählen nicht als Text: Was sie einsetzen, prüft der Versand am gerenderten Text
  const textProblem = (raw: string): string | null => {
    const top = stack[stack.length - 1];
    const text = raw.includes("{{") ? raw.replace(/\{\{[^{}]*\}\}/g, "") : raw;
    if (!top || text === "") return null;
    if ((TABLE_PARTS.has(top.tag) || top.tag === "ol" || top.tag === "ul") && /[^ \t\n\f\r]/.test(text)) return `nest:text@${top.tag}`;
    if (top.tag === "sup" || top.tag === "sub") {
      const map = top.tag === "sup" ? CONTRACT_SUP_CHARS : CONTRACT_SUB_CHARS;
      for (const ch of text) if (!Object.hasOwn(map, ch)) return `script:${ch}`;
    }
    return null;
  };
  for (const m of html.matchAll(TAG_RX())) {
    const t = textProblem(html.slice(last, m.index));
    if (t) return t;
    last = m.index + m[0].length;
    const close = m[1] === "/";
    const tag = m[2]!;
    if (close) {
      const at = stack.map((x) => x.tag).lastIndexOf(tag);
      if (at >= 0) stack.length = at;
      continue;
    }
    const top = stack[stack.length - 1];
    const parent = top?.tag ?? "";
    if (parent === "sup" || parent === "sub") return `nest:${parent}>${tag}`;
    if (/[ \t\r\n\f]class="tk-signature"/.test(m[3] ?? "") && stack.length > 0) return "nest:tk-signature";
    if (BLOCK_TAGS.has(tag) && NO_BLOCK_INSIDE.has(parent)) return `nest:${parent}>${tag}`;
    if (parent === "table") {
      const ph = Object.hasOwn(TABLE_PHASE, tag) ? TABLE_PHASE[tag]! : null;
      if (!ph) return `nest:table>${tag}`;
      if (top!.phase > ph[0]) return `order:${tag}`;
      top!.phase = ph[1];
    } else if (parent === "thead" || parent === "tbody" || parent === "tfoot") {
      if (tag !== "tr") return `nest:${parent}>${tag}`;
    } else if (parent === "tr") {
      if (tag !== "td" && tag !== "th") return `nest:tr>${tag}`;
    } else if (["caption", "thead", "tbody", "tfoot", "tr", "td", "th"].includes(tag)) {
      return `nest:${parent || "top"}>${tag}`;
    }
    if (tag === "li" && parent !== "ol" && parent !== "ul") return `nest:${parent || "top"}>li`;
    if (!VOID.has(tag)) stack.push({ tag, phase: 0 });
  }
  return textProblem(html.slice(last));
}

export function contractMarkupOk(html: string | null | undefined, opts: MarkupCheckOptions = {}): boolean {
  return contractMarkupProblem(html, opts) === null;
}

/** Verstoß gegen die Positivliste in Klartext für die Oberfläche (Kennung aus contractMarkupProblem). */
export function contractMarkupProblemText(problem: string, language: Language): string {
  const [kind, rest = ""] = [problem.slice(0, problem.indexOf(":")), problem.slice(problem.indexOf(":") + 1)];
  const de = language === "de";
  switch (kind) {
    case "char":
      if (rest === "invisible") return de ? "unsichtbare Zeichen im Text (etwa Nullbreite oder Soft-Hyphen, auch als Zeichenreferenz)" : "invisible characters in the text (such as zero width or soft hyphen, also as character reference)";
      if (rest === "control") return de ? "Steuerzeichen im Text" : "control characters in the text";
      return de ? "Steuerzeichen für die Schreibrichtung im Text (auch als Zeichenreferenz)" : "direction control characters in the text (also as character reference)";
    case "content": return de ? `${rest === "tk-page-break" ? "Seitenumbruch" : "Unterschriftsfeld"} mit Inhalt (nur leer erlaubt)` : `${rest === "tk-page-break" ? "page break" : "signature field"} with content (only allowed empty)`;
    case "print": return de ? `Zeichen „${rest}“ kann das PDF nicht drucken (etwa ein Emoji); bitte ersetzen` : `the PDF cannot print the character “${rest}” (such as an emoji); please replace it`;
    case "ref": return de ? `ungültige oder nicht unterstützte Zeichenreferenz „${rest}“ (ein „&“ im Text bitte als „&amp;“ schreiben)` : `invalid or unsupported character reference “${rest}” (please write an “&” in the text as “&amp;”)`;
    case "syntax": return de ? `nicht erlaubte Auszeichnung „${rest}“ (Kommentar, Großschreibung oder ein nicht maskiertes „<“)` : `markup not allowed “${rest}” (comment, upper case or an unescaped “<”)`;
    case "tag": return de ? `Element „${rest}“ ist nicht erlaubt` : `element “${rest}” is not allowed`;
    case "attr": return de ? `Attribut „${rest.split("@")[1] ?? rest}“ an „${rest.split("@")[0]}“ ist nicht erlaubt` : `attribute “${rest.split("@")[1] ?? rest}” on “${rest.split("@")[0]}” is not allowed`;
    case "class": return de ? `Klasse „${rest}“ ist nicht erlaubt` : `class “${rest}” is not allowed`;
    case "reserved": return de ? "die Kopfzeile des vertraulichen Teils setzt das System selbst" : "the header of the confidential part is set by the system";
    case "value": return de ? `ungültiger Wert für „${rest.split("@")[1] ?? rest}“` : `invalid value for “${rest.split("@")[1] ?? rest}”`;
    // Runde 2 (R2-06, R2-09, R2-10): Verschachtelung, die Bildschirm und PDF gleich zeigen
    case "nest":
      if (rest === "tk-signature") return de ? "Unterschriftsfeld in einer Tabelle, Liste oder einem Absatz (nur auf oberster Ebene erlaubt)" : "signature field inside a table, list or paragraph (only allowed at the top level)";
      if (rest.startsWith("text@")) return de ? `Text direkt in „${rest.slice(5)}“ (der Browser zeigt ihn an anderer Stelle als das PDF; bitte in eine Zelle bzw. einen Listenpunkt setzen)` : `text directly inside “${rest.slice(5)}” (the browser shows it elsewhere than the PDF; please put it into a cell or list item)`;
      return de ? `„${rest.split(">")[1] ?? rest}“ darf nicht in „${rest.split(">")[0]}“ stehen` : `“${rest.split(">")[1] ?? rest}” is not allowed inside “${rest.split(">")[0]}”`;
    case "order": return de ? `Tabellenteil „${rest}“ an falscher Stelle (Reihenfolge: Beschriftung, Kopf, Zeilen, Fuß)` : `table part “${rest}” in the wrong place (order: caption, head, rows, foot)`;
    case "script": return de ? `Zeichen „${rest}“ in Hoch- oder Tiefstellung (erlaubt sind Ziffern, + − = ( ) und einzelne Buchstaben)` : `character “${rest}” in superscript or subscript (allowed are digits, + − = ( ) and some letters)`;
    default: return problem;
  }
}

// ---------------------------------------------------------------------------
// Kopfzeile des vertraulichen Teils (VB-01, VB-20; Konzept 3.6 Stufe 1)
// ---------------------------------------------------------------------------

export const PART_NOTE_CLASS = "tk-part-note";

/** Reihenfolge der Parteien in der Kopfzeile: Marke, Creator, Agentur (wie im Vertragskopf). */
const PART_ORDER: readonly ContractRole[] = ["brand", "talent", "agency"];

const PART_WORDS: Record<Language, {
  label: Record<ContractRole, string>; nom: Record<ContractRole, string>; acc: Record<ContractRole, string>;
  /** Possessiv für „Mit ihrer/seiner Unterschrift“ (nur Deutsch) */
  poss: Record<ContractRole, string>;
  and: string;
}> = {
  de: {
    label: { brand: "Marke", talent: "Creator", agency: "Agentur" },
    nom: { brand: "die Marke", talent: "der Creator", agency: "die Agentur" },
    acc: { brand: "die Marke", talent: "den Creator", agency: "die Agentur" },
    poss: { brand: "ihrer", talent: "seiner", agency: "ihrer" },
    and: "und",
  },
  en: {
    label: { brand: "Brand", talent: "Creator", agency: "Agency" },
    nom: { brand: "the Brand", talent: "the Creator", agency: "the Agency" },
    acc: { brand: "the Brand", talent: "the Creator", agency: "the Agency" },
    poss: { brand: "", talent: "", agency: "" },
    and: "and",
  },
};

function joinRoles(roles: readonly ContractRole[], lang: Language): string {
  const w = PART_WORDS[lang];
  const names = roles.map((r) => w.label[r]);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} ${w.and} ${names[names.length - 1]}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export interface ConfidentialPartNoteText {
  /** z. B. „Vertraulicher Teil: Vereinbarung zwischen Marke und Agentur“ */
  title: string;
  /** „Nur für Marke und Agentur sichtbar.“ */
  visible: string;
  /** „Der Creator sieht diesen Teil nicht.“ */
  hidden: string;
  /** „Mit ihrer Unterschrift unter diesem Vertrag stimmen Marke und Agentur auch diesem Teil zu.“ (leer, wenn keine sehende Partei unterschreibt) */
  consent: string;
}

/**
 * Kopfzeile des vertraulichen Teils als Text, oder null, wenn keine Vertragspartei ausgeschlossen ist (dann ist der Teil eine
 * gewöhnliche Anlage, die alle sehen). `parties` sind die unterschreibenden Parteien (required_signers); die Agentur sieht den
 * Teil immer. Gleich in der Datenbank: app.contract_part_note (0111).
 */
export function confidentialPartNoteText(language: Language, hiddenFrom: readonly ContractRole[], parties: readonly ContractRole[]): ConfidentialPartNoteText | null {
  const lang: Language = language === "en" ? "en" : "de";
  const w = PART_WORDS[lang];
  const party = new Set(parties);
  const hidden = PART_ORDER.filter((r) => r !== "agency" && hiddenFrom.includes(r) && party.has(r));
  if (!hidden.length) return null;
  const viewers = PART_ORDER.filter((r) => r === "agency" || (party.has(r) && !hiddenFrom.includes(r)));
  const signing = viewers.filter((r) => party.has(r));
  if (lang === "de") {
    return {
      title: viewers.length >= 2 ? `Vertraulicher Teil: Vereinbarung zwischen ${joinRoles(viewers, lang)}` : "Vertraulicher Teil",
      visible: viewers.length >= 2 ? `Nur für ${joinRoles(viewers, lang)} sichtbar.` : `Nur für ${w.acc[viewers[0]!]} sichtbar.`,
      hidden: hidden.length >= 2 ? `${joinRoles(hidden, lang)} sehen diesen Teil nicht.` : `${capitalize(w.nom[hidden[0]!])} sieht diesen Teil nicht.`,
      consent: signing.length === 0 ? ""
        : signing.length >= 2 ? `Mit ihrer Unterschrift unter diesem Vertrag stimmen ${joinRoles(signing, lang)} auch diesem Teil zu.`
          : `Mit ${w.poss[signing[0]!]} Unterschrift unter diesem Vertrag stimmt ${w.nom[signing[0]!]} auch diesem Teil zu.`,
    };
  }
  return {
    title: viewers.length >= 2 ? `Confidential part: agreement between ${joinRoles(viewers, lang)}` : "Confidential part",
    visible: viewers.length >= 2 ? `Visible only to ${joinRoles(viewers, lang)}.` : `Visible only to ${w.acc[viewers[0]!]}.`,
    hidden: hidden.length >= 2 ? `${joinRoles(hidden, lang)} do not see this part.` : `${capitalize(w.nom[hidden[0]!])} does not see this part.`,
    consent: signing.length === 0 ? ""
      : signing.length >= 2 ? `By signing this contract, ${joinRoles(signing, lang)} also agree to this part.`
        : `By signing this contract, ${w.nom[signing[0]!]} also agrees to this part.`,
  };
}

/**
 * Kopfzeile als HTML (feste Texte, nur Auszeichnungen der Positivliste):
 * `<div class="tk-part-note"><p><strong>Titel</strong><br>Sätze</p></div>`. Null wie bei confidentialPartNoteText.
 */
export function confidentialPartNote(language: Language, hiddenFrom: readonly ContractRole[], parties: readonly ContractRole[]): string | null {
  const n = confidentialPartNoteText(language, hiddenFrom, parties);
  if (!n) return null;
  return `<div class="${PART_NOTE_CLASS}"><p><strong>${n.title}</strong><br>${[n.visible, n.hidden, n.consent].filter(Boolean).join(" ")}</p></div>`;
}

/** Hat der (gerenderte) vertrauliche Teil Inhalt? Nur Leerraum und Tags zählen nicht (wie renderDraft und contract_send). */
export function annexHasContent(html: string | null | undefined): boolean {
  return !!html && html.replace(CONTENTLESS_RX, "") !== "";
}
/** Tags, &nbsp; und Leerraum (ausdrückliche Klasse CONTRACT_WS_CLASS, gleich in der Datenbank; Befund 11) */
const CONTENTLESS_RX = new RegExp(`<[^>]*>|&nbsp;|${CONTRACT_WS_CLASS}`, "g");

/** Text ohne Tags, &nbsp; und Leerraum: leer, wenn nichts Sichtbares übrig ist (Endprüfung nach der letzten Unterschrift). */
export function contractVisibleRest(html: string): string {
  return html.replace(CONTENTLESS_RX, "");
}

/** Eine vorhandene Kopfzeile vorn am Teil entfernen (Kopie eines versendeten Vertrags); gleich app.contract_part_note_strip. */
export function stripConfidentialPartNote(annexHtml: string): string {
  return annexHtml.startsWith(`<div class="${PART_NOTE_CLASS}"><p>`) ? annexHtml.replace(/^<div class="tk-part-note"><p>[\s\S]*?<\/p><\/div>/, "") : annexHtml;
}

/**
 * Vertraulicher Teil mit Kopfzeile, genau wie ihn contract_send beim Versand speichert (app.contract_annex_with_note): eine
 * alte Kopfzeile fällt weg, die aktuelle steht vorn, wenn der Teil Inhalt hat und eine Vertragspartei ihn nicht sieht.
 * Für Vorschauen vor dem Versand.
 */
export function withConfidentialPartNote(annexHtml: string | null, language: Language, hiddenFrom: readonly ContractRole[], parties: readonly ContractRole[]): string | null {
  if (annexHtml === null) return null;
  const rest = stripConfidentialPartNote(annexHtml);
  if (!annexHasContent(rest)) return rest;
  const note = confidentialPartNote(language, hiddenFrom, parties);
  return note ? note + rest : rest;
}
